/**
 * Which destinations this role may open (TW-45774919).
 *
 * The sidebar used to answer this inline and the command palette did not ask at
 * all, so ⌘K offered every administrative page to every role while the sidebar
 * beside it was busy dimming the same rows. One hook because two lists drift:
 * the moment they disagree, one of them is offering a page that answers 403.
 *
 * Presentation only. Every route behind these entries is guarded and the API
 * refuses regardless of what got drawn, so this decides what is worth showing,
 * never what is allowed.
 */

import { useCallback } from "react";

import { mayManageRates } from "@/features/resale/rateScope";
import { useAuthStore } from "@/store/authStore";
import { mayReadMargin } from "@/features/resale/rateScope";
import { usePermissions } from "@/hooks/usePermissions";
import { canManagePermissionGroups, Role } from "@/lib/constants/roles";

/** Every href a nav list renders, children included; what the spec derives from. */
export function navHrefs(
  items: { href: string; children?: { href: string }[] }[],
): string[] {
  return items.flatMap((item) => [
    item.href,
    ...(item.children ?? []).map((c) => c.href),
  ]);
}

export function useNavAccess() {
  const { user } = useAuthStore();
  // The admin-tier flags come from `usePermissions`, which already derives
  // them from the same role; this hook used to compute a third copy.
  const {
    can,
    isAdmin,
    isSystemAdmin,
    isFullSystemAdmin,
    isDistributor,
    isMSPAdmin,
  } = usePermissions();

  const role = user?.role;
  const adminTier = isAdmin;

  const isAllowed = useCallback(
    (href: string): boolean => {
      switch (href) {
        // Each destination asks for its own module, and answers what the API
        // behind it answers. The table must be neither stricter nor looser than
        // the backend: stricter hides a page that works, looser offers a page
        // that answers 403, and both were measured against a running backend.
        //
        // `/scheduling` used to ask about `inventory` while the page behind it
        // checks `scheduling`, and `/reports` never looked at `reports`.
        case "/credentials":
          return adminTier || can("services", "view");
        // The alerts API honours the `alerts` grant (a company admin, or a
        // member whose group grants the module), so the row is offered on it.
        // It was admin-tier only for a while, when the API was admin-only and
        // the grant bought a row that opened to a 403.
        case "/alerts":
          return adminTier || can("alerts", "view");
        // Accounting and read-only are named because the backend names them:
        // `FinOpsOperationalOrGroupPermission` gives both roles baseline read
        // on inventory, schedules and recommendations with no group grant, and
        // the pages render for them. Hiding the rows made the menu stricter than
        // the API, which is the same defect as offering a refused page, seen
        // from the other side.
        case "/inventory":
          return (
            adminTier ||
            role === Role.ACCOUNTING ||
            role === Role.READ_ONLY ||
            can("inventory", "view")
          );
        case "/scheduling":
          return (
            adminTier ||
            role === Role.ACCOUNTING ||
            role === Role.READ_ONLY ||
            can("scheduling", "view")
          );
        case "/recommendations":
          return (
            adminTier ||
            role === Role.ACCOUNTING ||
            role === Role.READ_ONLY ||
            can("recommendations", "view")
          );
        case "/costs":
        case "/costs/explorer":
          return (
            adminTier ||
            role === Role.ACCOUNTING ||
            role === Role.READ_ONLY ||
            can("cost_analysis", "view")
          );
        case "/costs/margin":
          return mayReadMargin(role);
        // Reports reads cost data, so a role granted `cost_analysis` keeps it
        // even without `reports` of its own. That is the answer this gave
        // before, preserved deliberately rather than tightened in passing.
        case "/reports":
          return (
            adminTier ||
            role === Role.ACCOUNTING ||
            role === Role.READ_ONLY ||
            can("reports", "view") ||
            can("cost_analysis", "view")
          );
        // Only the levels that resell cloud have anyone to bill.
        case "/billing":
          return isSystemAdmin || isDistributor || isMSPAdmin;

        // Administrative destinations. These are the ones worth hiding rather
        // than dimming: a greyed-out "Impersonation" row told every MSP admin
        // the capability exists and that somebody above them holds it.
        case "/admin/platform-analytics":
          return isSystemAdmin || isDistributor;
        case "/admin/organizations":
        case "/admin/approvals":
          return isSystemAdmin || isDistributor || isMSPAdmin;
        case "/admin/groups":
          return canManagePermissionGroups(role);
        // The rate surface: platform staff, distributors and MSPs. Same rule
        // the page and its admin card use, so the three cannot disagree.
        case "/admin/rates":
          return mayManageRates(role);
        case "/admin/impersonation":
          return isFullSystemAdmin;
        // The admin entrance and its user list: every admin tier runs these for
        // its own organization, and nobody below that tier has any business
        // being offered them. The sidebar reached the same answer by only
        // rendering its admin list behind an admin-tier check; the palette had
        // no such gate and listed them for everyone.
        case "/admin":
        case "/admin/users":
          return adminTier;

        // Reachable by anyone signed in: the dashboard, the hub, settings and
        // help. These four are named rather than left to the default, because
        // the default no longer says yes.
        case "/":
        case "/capabilities":
        case "/settings":
        case "/help":
          return true;

        // Anything not named above is not offered.
        //
        // This returned `true`, which is fail-open: a page added to either nav
        // list was offered to everybody until somebody noticed.
        //
        // A page added to either nav list now stays invisible until its rule
        // lands here, which fails toward a missing row rather than toward a row
        // that answers 403. `useNavAccess.test.ts` asserts every href both
        // controls render is named above, so the missing row is loud.
        default:
          return false;
      }
    },
    [
      adminTier,
      can,
      isDistributor,
      isFullSystemAdmin,
      isMSPAdmin,
      isSystemAdmin,
      role,
    ],
  );

  /**
   * A list with the unreachable entries removed, children included.
   *
   * A section goes with its children: a parent whose own permission fails is
   * dropped whole, and one that survives keeps only the children this role may
   * open, so a group never renders empty and never heads a list of pages that
   * are not there.
   */
  const visibleNav = useCallback(
    <T extends { href: string; children?: T[] }>(items: T[]): T[] =>
      items
        .filter((item) => isAllowed(item.href))
        .map((item) =>
          item.children
            ? {
                ...item,
                children: item.children.filter((c) => isAllowed(c.href)),
              }
            : item,
        ),
    [isAllowed],
  );

  return { isAllowed, visibleNav };
}
